import React, { useMemo } from 'react';
import { Form, Input } from 'antd';
import {
  TINGANTA_NICH_KARMANI_TABLES,
  TINGANTA_NICH_KARTARI_ATMANEPAD_TABLES,
  TINGANTA_NICH_KARTARI_PARASMAI_TABLES,
  TINGANTA_RUP_KARMANI_TABLES,
  TINGANTA_RUP_KARTARI_ATMANEPAD_TABLES,
  TINGANTA_RUP_KARTARI_PARASMAI_TABLES,
  TINGANTA_SAN_KARMANI_TABLES,
  TINGANTA_SAN_KARTARI_ATMANEPAD_TABLES,
  TINGANTA_SAN_KARTARI_PARASMAI_TABLES,
} from '../constants/tingantaRupFieldKeys';

const HEADER_BG = '#b8d0eb';
const SUBHEADER_BG = '#cfe2f3';
const CELL_BORDER = '#d4dde8';

/** Column: singular / dual / plural. */
const VACHANA_COL_LABELS = ['एक वचन', 'द्विवचन', 'बहुवचन'] as const;

/** Row: प्रथम / मध्यम / उत्तम (short labels). */
const PURUSHA_ROW_LABELS = ['प्र', 'म', 'उ'] as const;

export function splitNinefold(raw: string | undefined): string[] {
  const t = raw == null ? '' : String(raw);
  if (!t.trim()) return Array.from({ length: 9 }, () => '');
  const parts = t.split(';').map((p) => p.trim());
  const out = parts.slice(0, 9);
  while (out.length < 9) out.push('');
  return out;
}

export function joinNinefold(cells: string[]): string {
  return cells.map((c) => (c == null ? '' : String(c))).join(';');
}

type LakaraNinefoldTableProps = {
  header: string;
  value?: string;
  onChange?: (v: string) => void;
};

function LakaraNinefoldTable({ header, value, onChange }: LakaraNinefoldTableProps) {
  const cells = useMemo(() => splitNinefold(value), [value]);

  const setCell = (index: number, text: string) => {
    const next = [...cells];
    next[index] = text;
    onChange?.(joinNinefold(next));
  };

  const cornerCellStyle: React.CSSProperties = {
    background: SUBHEADER_BG,
    border: `1px solid ${CELL_BORDER}`,
    width: 44,
    minWidth: 44,
  };
  const axisLabelStyle: React.CSSProperties = {
    background: SUBHEADER_BG,
    textAlign: 'center',
    padding: '8px 6px',
    fontSize: 13,
    border: `1px solid ${CELL_BORDER}`,
    fontWeight: 500,
  };

  return (
    <table
      style={{
        width: '100%',
        borderCollapse: 'collapse',
        marginBottom: 20,
        tableLayout: 'fixed',
      }}
    >
      <thead>
        <tr>
          <td
            colSpan={4}
            style={{
              background: HEADER_BG,
              textAlign: 'center',
              padding: '10px 12px',
              fontSize: 15,
              border: `1px solid ${CELL_BORDER}`,
            }}
          >
            {header}
          </td>
        </tr>
        <tr>
          <td style={cornerCellStyle} aria-hidden />
          {VACHANA_COL_LABELS.map((label) => (
            <td key={label} style={axisLabelStyle}>
              {label}
            </td>
          ))}
        </tr>
      </thead>
      <tbody>
        {[0, 1, 2].map((row) => (
          <tr key={row}>
            <td style={{ ...axisLabelStyle, width: 44, verticalAlign: 'middle' }}>
              {PURUSHA_ROW_LABELS[row]}
            </td>
            {[0, 1, 2].map((col) => {
              const i = row * 3 + col;
              return (
                <td
                  key={i}
                  style={{
                    border: `1px solid ${CELL_BORDER}`,
                    padding: 0,
                    verticalAlign: 'top',
                    background: '#fff',
                  }}
                >
                  <Input.TextArea
                    value={cells[i]}
                    onChange={(e) => setCell(i, e.target.value)}
                    autoSize={{ minRows: 1, maxRows: 6 }}
                    variant="borderless"
                    style={{
                      width: '100%',
                      fontSize: 14,
                      lineHeight: 1.45,
                      padding: '8px 10px',
                    }}
                  />
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function KartariLakaraFormItems({
  tables,
}: {
  tables: readonly { field: string; header: string }[];
}) {
  return (
    <>
      {tables.map(({ field, header }) => (
        <Form.Item key={field} name={field} style={{ marginBottom: 0 }}>
          <LakaraNinefoldTable header={header} />
        </Form.Item>
      ))}
    </>
  );
}

/** Parasmaipada lakāra blocks for rūpa kartari: nine cells per field, stored as `;`-separated string. */
export function TingantaRupKartariParasmaiFormItems() {
  return <KartariLakaraFormItems tables={TINGANTA_RUP_KARTARI_PARASMAI_TABLES} />;
}

/** Ātmanepad lakāra blocks for rūpa kartari — same grid as parasmaipada. */
export function TingantaRupKartariAtmanepadFormItems() {
  return <KartariLakaraFormItems tables={TINGANTA_RUP_KARTARI_ATMANEPAD_TABLES} />;
}

/** Rūpa karmani (१० lakārāḥ, कर्मणि). */
export function TingantaRupKarmaniFormItems() {
  return <KartariLakaraFormItems tables={TINGANTA_RUP_KARMANI_TABLES} />;
}

/** San kartari — parasmaipada. */
export function TingantaSanKartariParasmaiFormItems() {
  return <KartariLakaraFormItems tables={TINGANTA_SAN_KARTARI_PARASMAI_TABLES} />;
}

/** San kartari — ātmanepad. */
export function TingantaSanKartariAtmanepadFormItems() {
  return <KartariLakaraFormItems tables={TINGANTA_SAN_KARTARI_ATMANEPAD_TABLES} />;
}

/** San karmani. */
export function TingantaSanKarmaniFormItems() {
  return <KartariLakaraFormItems tables={TINGANTA_SAN_KARMANI_TABLES} />;
}

/** Ṇij kartari — parasmaipada. */
export function TingantaNichKartariParasmaiFormItems() {
  return <KartariLakaraFormItems tables={TINGANTA_NICH_KARTARI_PARASMAI_TABLES} />;
}

/** Ṇij kartari — ātmanepad. */
export function TingantaNichKartariAtmanepadFormItems() {
  return <KartariLakaraFormItems tables={TINGANTA_NICH_KARTARI_ATMANEPAD_TABLES} />;
}

/** Ṇij karmani. */
export function TingantaNichKarmaniFormItems() {
  return <KartariLakaraFormItems tables={TINGANTA_NICH_KARMANI_TABLES} />;
}
